function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import React, { useCallback, useEffect, useMemo } from 'react';
import parse from 'html-react-parser';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectEventDirection, selectEventMessageText, selectTicketMessageAttachments } from 'flow/selectors/TicketEventSelectors';
import { EmailTicketMessageAttachment } from '../Messages/TicketMessageAttachment';
import { getNodeText } from './LinkElementConverter';
import { convertPlainTextNewlines, getSanitizedHtml } from './email-sanitize-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var EmailBody = function EmailBody(_ref) {
  var ticketKey = _ref.ticketKey,
    eventKey = _ref.eventKey,
    sentTime = _ref.sentTime,
    onAttachmentsInlined = _ref.onAttachmentsInlined;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var _useModal = useModal(),
    openModal = _useModal.openModal;
  var message = useAppSelector(function (state) {
    return selectEventMessageText(state, ticketKey, eventKey);
  });
  var attachments = useAppSelector(function (state) {
    return selectTicketMessageAttachments(state, ticketKey, eventKey);
  });
  var direction = useAppSelector(function (state) {
    return selectEventDirection(state, ticketKey, eventKey);
  });

  // Remote (hotlinked) images are only auto-loaded for OUTGOING messages,
  // whose HTML is agent-authored (composed reply + signature). INCOMING
  // bodies are sender-controlled: rendering <img src> would fire a GET to an
  // arbitrary URL the moment the email is opened (tracking pixels, CSRF via
  // GET). NOTE: this trust rests on outbound event bodies staying
  // agent-authored — if stored outbound bodies ever start embedding quoted
  // thread history, this gate must be revisited.
  var trustRemoteImages = direction === 'OUTGOING';
  var contentSanitized = useMemo(function () {
    return getSanitizedHtml(convertPlainTextNewlines(message));
  },
  // THIS IS IMPORTANT. UNDER NO CIRCUMSTANCES do we want to render unsanitized HTML
  [message]);

  // Links in email content are never followed directly: clicking one opens a
  // confirmation dialog showing the real destination, so the recipient can
  // verify where it leads before we open it in a new tab.
  var handleLinkClick = useCallback(function (url) {
    openModal('confirmationDialog', {
      title: i18next.t('app.confirm'),
      description: /*#__PURE__*/_jsxs(_Fragment, {
        children: [i18next.t('tickets.message.link.confirm_body'), /*#__PURE__*/_jsx("span", {
          className: "text-link mt-1 block font-medium break-all",
          children: url
        }), /*#__PURE__*/_jsx("span", {
          className: "mt-2 block",
          children: i18next.t('tickets.message.link.confirm_question')
        })]
      }),
      confirmButtonText: i18next.t('tickets.message.link.open'),
      handleAccept: function handleAccept() {
        return window.open(url, '_blank', 'noopener,noreferrer');
      }
    });
  }, [openModal, i18next]);
  var _useMemo = useMemo(function () {
      return renderEmailHtml(contentSanitized, {
        attachments: attachments,
        sentTime: sentTime,
        i18next: i18next,
        onLinkClick: handleLinkClick,
        trustRemoteImages: trustRemoteImages
      });
    }, [contentSanitized, attachments, sentTime, i18next, handleLinkClick, trustRemoteImages]),
    parsedContent = _useMemo.parsedContent,
    inlinedKeys = _useMemo.inlinedKeys;
  useEffect(function () {
    onAttachmentsInlined(inlinedKeys);
  }, [inlinedKeys, onAttachmentsInlined]);
  return (
    /*#__PURE__*/
    // Tailwind's preflight strips default browser styles (margins, list-style, etc.) from HTML elements.
    // Since we render sanitized email HTML, we restore essential spacing and styling for block-level elements.
    // whitespace-normal overrides the inherited whitespace-pre-wrap, which otherwise renders
    // whitespace between HTML tags as visible lines (e.g. nested empty divs each adding 24px of height).
    _jsx("div", {
      className: ['whitespace-normal', '[&_p]:my-6', '[&_h1]:my-6 [&_h2]:my-5 [&_h3]:my-4', '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:ps-6', '[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:ps-6', '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:ps-4'].join(' '),
      children: parsedContent
    })
  );
};
export default /*#__PURE__*/React.memo(EmailBody);
// Parses sanitized email HTML into React nodes, replacing <img>/<a> elements
// with our own components. Kept at module scope (not inside EmailBody) so the
// per-element render helpers aren't redefined on every render. Returns the
// parsed content and the set of attachment keys that were rendered inline.
var renderEmailHtml = function renderEmailHtml(contentSanitized, _ref2) {
  var attachments = _ref2.attachments,
    sentTime = _ref2.sentTime,
    i18next = _ref2.i18next,
    onLinkClick = _ref2.onLinkClick,
    trustRemoteImages = _ref2.trustRemoteImages;
  var inlinedKeys = new Set();

  // Intercept link clicks so they go through the confirmation dialog instead
  // of navigating directly.
  var onConfirmLinkClick = function onConfirmLinkClick(href) {
    return function (event) {
      event.preventDefault();
      onLinkClick(href);
    };
  };
  var renderImgNode = function renderImgNode(imgNode) {
    var _imgNode$attribs3;
    var interactive = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var attachmentToDisplayInline = getAttachmentMatchingImgTag(imgNode, attachments);
    if (attachmentToDisplayInline) {
      var _imgNode$attribs, _imgNode$attribs2;
      inlinedKeys.add(attachmentToDisplayInline.get('key'));
      var imgWidth = parseImgDimension((_imgNode$attribs = imgNode.attribs) === null || _imgNode$attribs === void 0 ? void 0 : _imgNode$attribs.width);
      var imgHeight = parseImgDimension((_imgNode$attribs2 = imgNode.attribs) === null || _imgNode$attribs2 === void 0 ? void 0 : _imgNode$attribs2.height);
      // Matching attachment found - replace <img> with EmailTicketMessageAttachment component
      return /*#__PURE__*/_jsx(EmailTicketMessageAttachment, {
        attachment: attachmentToDisplayInline,
        sentTime: sentTime,
        htmlWidth: imgWidth,
        htmlHeight: imgHeight,
        interactive: interactive
      }, "ebma-".concat(attachmentToDisplayInline.get('key')));
    }

    // No matching attachment \u2014 handle by URI scheme:
    // \u2022 data:image/ (excluding SVG): render inline (no network request, SVG excluded for script safety)
    // \u2022 https on OUTGOING (agent-authored) messages: render directly (signatures use external image URLs)
    // \u2022 https on INCOMING messages: TO BE CAUTIOUS, we do NOT render the <img> with its src \u2014 that
    //   would fire a GET to a sender-controlled URL on open. Shown as a confirm-on-click link instead.
    // \u2022 Other protocols (http, ftp, etc.): broken image with alt text for troubleshooting

    var srcUrl = (_imgNode$attribs3 = imgNode.attribs) === null || _imgNode$attribs3 === void 0 ? void 0 : _imgNode$attribs3.src;
    if (DATA_IMAGE_ALLOWED.test(srcUrl !== null && srcUrl !== void 0 ? srcUrl : '')) {
      var _imgNode$attribs4, _imgNode$attribs5, _imgNode$attribs6;
      var _imgWidth = parseImgDimension((_imgNode$attribs4 = imgNode.attribs) === null || _imgNode$attribs4 === void 0 ? void 0 : _imgNode$attribs4.width);
      var _imgHeight = parseImgDimension((_imgNode$attribs5 = imgNode.attribs) === null || _imgNode$attribs5 === void 0 ? void 0 : _imgNode$attribs5.height);
      return /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx("img", {
          src: srcUrl,
          alt: ((_imgNode$attribs6 = imgNode.attribs) === null || _imgNode$attribs6 === void 0 ? void 0 : _imgNode$attribs6.alt) || '',
          width: _imgWidth,
          height: _imgHeight
        }), /*#__PURE__*/_jsx("span", {
          className: "block text-xs text-gray-500 italic",
          children: i18next.t('tickets.message.image.embedded_info')
        })]
      });
    }
    if (srcUrl !== null && srcUrl !== void 0 && srcUrl.startsWith(URI.HTTPS)) {
      if (trustRemoteImages) {
        var _imgNode$attribs7, _imgNode$attribs8, _imgNode$attribs9;
        var _imgWidth2 = parseImgDimension((_imgNode$attribs7 = imgNode.attribs) === null || _imgNode$attribs7 === void 0 ? void 0 : _imgNode$attribs7.width);
        var _imgHeight2 = parseImgDimension((_imgNode$attribs8 = imgNode.attribs) === null || _imgNode$attribs8 === void 0 ? void 0 : _imgNode$attribs8.height);
        return /*#__PURE__*/_jsx("img", {
          src: srcUrl,
          alt: ((_imgNode$attribs9 = imgNode.attribs) === null || _imgNode$attribs9 === void 0 ? void 0 : _imgNode$attribs9.alt) || '',
          width: _imgWidth2,
          height: _imgHeight2,
          style: {
            maxWidth: '100%'
          }
        });
      }
      return /*#__PURE__*/_jsx("a", {
        href: srcUrl,
        onClick: onConfirmLinkClick(srcUrl),
        className: "text-link cursor-pointer",
        children: srcUrl
      });
    }

    // For other protocols (http (non-secure), ftp, etc.), we just render the src URL with a small broken image icon. This for troubleshooting purposes.
    return /*#__PURE__*/_jsx("img", {
      className: "italic",
      alt: srcUrl
    });
  };
  var renderLinkNode = function renderLinkNode(linkNode) {
    var _linkNode$attribs, _getNodeText2;
    // <a> tags: special handling to only render https:// mailto: and tel: links as actual links
    var href = (_linkNode$attribs = linkNode.attribs) === null || _linkNode$attribs === void 0 ? void 0 : _linkNode$attribs.href;
    if (!href) {
      // No href (empty, missing, or stripped by DOMPurify for dangerous schemes like javascript:) \u2192 show link text only
      return /*#__PURE__*/_jsx("span", {
        children: getNodeText(linkNode)
      });
    }
    if (!URI_A_TAG_ALLOWED_LIST.test(href)) {
      var _getNodeText;
      // href is not an allowed scheme (https://, mailto:, tel:) \u2192 show the URI as plain italic text so it's visible but not clickable
      var _linkText = (_getNodeText = getNodeText(linkNode)) === null || _getNodeText === void 0 ? void 0 : _getNodeText.trim();
      var textIsRedundant = !_linkText || href.includes(_linkText);
      return /*#__PURE__*/_jsxs("span", {
        className: "italic",
        children: [!textIsRedundant && "".concat(_linkText, ": "), href]
      });
    }
    // href is an allowed scheme \u2192 render its own text (or the URL if it has
    // none) as a link; clicking confirms the destination before opening, so
    // we no longer need to spell the raw URL out beside mismatched text.
    var linkText = (_getNodeText2 = getNodeText(linkNode)) === null || _getNodeText2 === void 0 ? void 0 : _getNodeText2.trim();
    return /*#__PURE__*/_jsx("a", {
      href: href,
      onClick: onConfirmLinkClick(href),
      className: "text-link cursor-pointer",
      children: linkText || href
    });
  };
  var parsedContent = parse(contentSanitized, {
    replace: function replace(domNode) {
      if (isEmptyParagraph(domNode)) {
        return /*#__PURE__*/_jsx(_Fragment, {});
      }
      if (elementIsLink(domNode)) {
        var imgNode = _findImgDescendant(domNode);
        var matchedAttachment = imgNode && getAttachmentMatchingImgTag(imgNode, attachments);
        if (imgNode && matchedAttachment) {
          var _domNode$attribs, _getNodeText3;
          // Linked embedded image (e.g. a logo or social-media icon in
          // a signature). Only embedded (cid) attachments are inlined
          // here; external/hotlinked images fall through to the link so
          // we neither leak a request nor clutter the link with a long
          // image URL.
          var image = renderImgNode(imgNode, false);
          var href = (_domNode$attribs = domNode.attribs) === null || _domNode$attribs === void 0 ? void 0 : _domNode$attribs.href;
          var linkText = (_getNodeText3 = getNodeText(domNode)) === null || _getNodeText3 === void 0 ? void 0 : _getNodeText3.trim();
          if (!linkText) {
            // Image-only link: the image itself is the link. Clicking
            // confirms the destination before opening; no URL text is
            // printed beside it. Unsafe/missing hrefs render the image
            // alone.
            if (href && URI_A_TAG_ALLOWED_LIST.test(href)) {
              return /*#__PURE__*/_jsx("a", {
                href: href,
                onClick: onConfirmLinkClick(href),
                className: "inline-block cursor-pointer",
                children: image
              });
            }
            return image;
          }

          // Image with visible link text: render the image beside the
          // (confirm-on-click) text link. min-w-0 + wrap-break-word lets
          // long text wrap instead of overflowing the nowrap flex row,
          // while the icon keeps its size.
          return /*#__PURE__*/_jsxs("span", {
            className: "inline-flex max-w-full items-center gap-2",
            children: [/*#__PURE__*/_jsx("span", {
              className: "shrink-0",
              children: image
            }), /*#__PURE__*/_jsx("span", {
              className: "min-w-0 wrap-break-word",
              children: renderLinkNode(domNode)
            })]
          });
        }
        return renderLinkNode(domNode);
      }
      if (elementIsImg(domNode)) {
        return renderImgNode(domNode);
      }
      return domNode;
    }
  });
  return {
    parsedContent: parsedContent,
    inlinedKeys: inlinedKeys
  };
};
var NBSP_PATTERN = /\u00A0/g;

// Tags that render something regardless of their (lack of) children, so a
// paragraph containing one is never "empty".
var CONTENTFUL_TAGS = new Set(['img', 'hr', 'video', 'audio', 'iframe']);

// A node contributes no visible content if it's whitespace/&nbsp; text, a <br>,
// or an inline wrapper (e.g. <span>, <b>) whose children are all blank. This is
// recursive so <p><span>&nbsp;</span></p> and nested wrappers count as empty.
var _nodeIsBlank = function nodeIsBlank(node) {
  if (node.type === 'tag') {
    var _node$children;
    if (node.name === 'br') return true;
    if (CONTENTFUL_TAGS.has(node.name)) return false;
    return ((_node$children = node.children) !== null && _node$children !== void 0 ? _node$children : []).every(_nodeIsBlank);
  }
  if (node.type === 'text') {
    return node.data.replace(NBSP_PATTERN, '').trim() === '';
  }
  return false;
};
var isEmptyParagraph = function isEmptyParagraph(element) {
  var _element$children;
  if ((element === null || element === void 0 ? void 0 : element.type) !== 'tag' || element.name !== 'p') return false;
  return ((_element$children = element.children) !== null && _element$children !== void 0 ? _element$children : []).every(_nodeIsBlank);
};
var elementIsImg = function elementIsImg(element) {
  return (element === null || element === void 0 ? void 0 : element.type) === 'tag' && element.name === 'img';
};
var elementIsLink = function elementIsLink(element) {
  return (element === null || element === void 0 ? void 0 : element.type) === 'tag' && element.name === 'a';
};

// Finds the first <img> within an element's subtree (e.g. an <a> wrapping an
// icon). Returns undefined if the subtree contains no image.
var _findImgDescendant = function findImgDescendant(element) {
  var _element$children2;
  var _iterator = _createForOfIteratorHelper((_element$children2 = element.children) !== null && _element$children2 !== void 0 ? _element$children2 : []),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var child = _step.value;
      if (child.type !== 'tag') continue;
      if (child.name === 'img') return child;
      var nested = _findImgDescendant(child);
      if (nested) return nested;
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return undefined;
};
var getAttachmentMatchingImgTag = function getAttachmentMatchingImgTag(element, attachments) {
  var _element$attribs;
  if (!elementIsImg(element) || !attachments) {
    return null;
  }

  // Match img src with attachment contentId
  var src = stripCidPrefix((_element$attribs = element.attribs) === null || _element$attribs === void 0 ? void 0 : _element$attribs.src);
  return attachments.find(function (attachment) {
    return src && attachment.get('contentId') === src;
  });
};
var stripCidPrefix = function stripCidPrefix(value) {
  return value !== null && value !== void 0 && value.startsWith(URI.CONTENT_ID) ? value.slice(URI.CONTENT_ID.length) : value;
};
var DIGITS_ONLY = /^\d+$/;
var parseImgDimension = function parseImgDimension(value) {
  if (!value || !DIGITS_ONLY.test(value)) return undefined;
  return parseInt(value, 10) || undefined;
};
var URI_A_TAG_ALLOWED_LIST = /^(https:\/\/|mailto:|tel:)/i;

// Matches data:image/ URIs (e.g. data:image/png;base64,...) but excludes SVG for defense-in-depth
// (SVG can contain scripts — browsers sandbox SVG in <img>, but we err on the side of caution).
var DATA_IMAGE_ALLOWED = /^data:image\/(?!svg\+xml)[a-z]+[;,]/i;
var URI = Object.freeze({
  HTTPS: 'https://',
  CONTENT_ID: 'cid:',
  MAILTO: 'mailto:',
  TEL: 'tel:'
});